import { CANVAS_WIDTH } from "../../game/constants";
import { isMobileViewport } from "../../game/platform";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import {
  DAILY_LOGIN_REWARDS,
  DAILY_REWARDS_CYCLE,
  wrapToCycleDay,
} from "../../economy/dailyLoginRewards";
import {
  GAME_FONT,
  MENU_CONTENT_OFFSET_PX,
  mFont,
  roundedRectPath,
} from "../renderConstants";
import { drawButton } from "../ui";

// ── Types ────────────────────────────────────────────────────────────────

export interface DailyLoginModalDeps {
  ctx: CanvasRenderingContext2D;
  canvasH: number;
  isGuestUser: boolean;
  nowSec: () => number;
  measureTextWidth: (text: string) => number;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawCircleCloseButton: (rect: Rect) => void;
  drawCoinIcon: (x: number, y: number, size: number) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
  drawDailyLoginStamp: (rect: Rect) => void;
  drawGlowPulse: (rect: Rect, color: string, maxBlur: number) => void;
  crateClosedImage?: HTMLImageElement | null;
  crateOpenImage?: HTMLImageElement | null;
}

// ── Tier border colors (matching daily rewards page) ──────────────────

const TIER_BORDER: Record<string, string> = {
  normal: "#6f94c5",
  bigger: "#b44aff",
  substantial: "#ffd700",
};

// ── Extracted screen function ────────────────────────────────────────────

export function drawDailyLoginModalScreen(deps: DailyLoginModalDeps, model: RenderModel): Partial<UiRects> {
  const ctx = deps.ctx;
  const mobile = isMobileViewport();
  const status = model.dailyLoginStatus;
  const canvasH = deps.canvasH;
  const isGuestUser = deps.isGuestUser;
  const currentDay = status?.currentDay ?? 0;
  const cycleDay = wrapToCycleDay(currentDay);
  const canClaim = !isGuestUser && status?.canClaimToday === true;
  const claimableCycleDay = status?.claimableDay != null ? wrapToCycleDay(status.claimableDay) : null;

  // ── Dismiss backdrop ──
  const dismissRect: Rect = {
    x: 0,
    y: -MENU_CONTENT_OFFSET_PX,
    w: CANVAS_WIDTH,
    h: canvasH + MENU_CONTENT_OFFSET_PX,
  };
  ctx.save();
  ctx.fillStyle = "rgba(4,10,24,0.78)";
  ctx.fillRect(dismissRect.x, dismissRect.y, dismissRect.w, dismissRect.h);

  // ── Panel ──
  const panelW = mobile ? 310 : 312;
  const panelH = mobile ? 440 : 420;
  const panelX = Math.floor((CANVAS_WIDTH - panelW) / 2);
  const panelY = Math.max(8, Math.floor((canvasH - panelH) / 2) - 20);

  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 6);
  ctx.fillStyle = "rgba(20,36,54,0.85)";
  ctx.fill();
  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 6);
  ctx.strokeStyle = "rgba(108,200,255,0.6)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // ── Title ──
  const titleY = panelY + 16;
  ctx.fillStyle = "#fff2c1";
  ctx.font = `bold ${mobile ? 9 : 10}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("DAILY REWARDS", panelX + panelW / 2, titleY);

  // ── Subtitle ──
  ctx.fillStyle = "#9fc6f7";
  ctx.font = `${mobile ? 4 : 5}px ${GAME_FONT}`;
  ctx.fillText("UTC MIDNIGHT RESET", panelX + panelW / 2, titleY + (mobile ? 13 : 14));

  // ── Close button (circle X, matching lobby/shop) ──
  const closeSz = mobile ? 34 : 24;
  const closeRect: Rect = { x: panelX + panelW - closeSz - 6, y: panelY + 6, w: closeSz, h: closeSz };
  deps.drawCircleCloseButton(closeRect);

  // ── Card grid: 3-3-1 layout (matching daily rewards page) ──
  const gridPad = 10;
  const gridW = panelW - gridPad * 2;
  const cols = 3;
  const gapX = 6;
  const gapY = 6;
  const cardW = Math.floor((gridW - gapX * (cols - 1)) / cols);
  const cardH = cardW;
  const gridX = panelX + gridPad;
  const gridTopY = panelY + (mobile ? 38 : 40);
  const day7H = Math.floor(cardH * 0.7);

  let claimRect: Rect | undefined;

  for (let i = 0; i < DAILY_REWARDS_CYCLE; i++) {
    const reward = DAILY_LOGIN_REWARDS[i]!;
    const dayNum = i + 1;
    const isLastRow = dayNum === 7;
    const col = isLastRow ? 0 : i % cols;
    const row = isLastRow ? 2 : Math.floor(i / cols);
    const cw = isLastRow ? gridW : cardW;
    const ch = isLastRow ? day7H : cardH;
    const cx = isLastRow ? gridX : gridX + col * (cardW + gapX);
    const cy = gridTopY + row * (cardH + gapY);

    const isClaimed = dayNum < cycleDay || (dayNum === cycleDay && !canClaim);
    const isClaimable = canClaim && dayNum === claimableCycleDay;

    if (isClaimable) {
      claimRect = { x: cx, y: cy, w: cw, h: ch };
    }

    // ── Pulsing glow for claimable ──
    if (isClaimable) {
      const pulse = 0.5 + 0.5 * Math.sin(deps.nowSec() * 3);
      ctx.save();
      ctx.shadowColor = TIER_BORDER[reward.tier] ?? "#6f94c5";
      ctx.shadowBlur = 10 + pulse * 10;
      roundedRectPath(ctx, cx - 1, cy - 1, cw + 2, ch + 2, 8);
      ctx.fillStyle = "rgba(100,246,255,0.12)";
      ctx.fill();
      ctx.restore();
    }

    // ── Crate image ──
    const crateImg = isClaimed ? (deps.crateOpenImage ?? null) : (deps.crateClosedImage ?? null);
    const crateSz = isLastRow ? Math.floor(ch * 0.85) : Math.floor(cw * 0.85);
    const crateDrawX = cx + (cw - crateSz) / 2;
    const crateDrawY = cy + (ch - crateSz) / 2 + 4;

    ctx.save();
    if (isClaimed || (!isClaimable && !isGuestUser)) {
      ctx.filter = "grayscale(0.9) brightness(0.3)";
    }
    if (crateImg != null) {
      ctx.drawImage(crateImg, crateDrawX, crateDrawY, crateSz, crateSz);
    }
    ctx.restore();

    // ── Day label ──
    const dayFontPx = mobile ? 5 : 5;
    ctx.fillStyle = isClaimable ? "#ffffff" : isClaimed ? "#6a7a8a" : "#c0d0e0";
    ctx.font = `bold ${dayFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(`DAY ${dayNum}`, cx + cw / 2, crateDrawY + 2);

    // ── Reward amounts (show for unclaimed, non-claimable) ──
    if (!isClaimed && !isClaimable) {
      const rIconSz = 10;
      const rFontSz = 6;
      const iconGap = isLastRow ? 28 : 16;
      const rMidX = cx + cw / 2;
      const rMidY = cy + ch / 2 + 4;

      const coinCX = rMidX - iconGap / 2;
      deps.drawCoinIcon(coinCX - rIconSz / 2, rMidY - rIconSz / 2, rIconSz);
      ctx.fillStyle = "#ffd700";
      ctx.font = `bold ${rFontSz}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(`${reward.coins}`, coinCX, rMidY + rIconSz / 2 + 1);

      const gemCX = rMidX + iconGap / 2;
      deps.drawGemIcon(gemCX - rIconSz / 2, rMidY - rIconSz / 2, rIconSz);
      ctx.fillStyle = "#64f6ff";
      ctx.fillText(`${reward.gems}`, gemCX, rMidY + rIconSz / 2 + 1);
    }

    // ── CLAIM chip for claimable day ──
    if (isClaimable) {
      const chipW = 24;
      const chipH = 10;
      const chipX = cx + cw / 2 - chipW / 2;
      const chipY = cy + ch - chipH - 4;
      roundedRectPath(ctx, chipX, chipY, chipW, chipH, 4);
      ctx.fillStyle = "rgba(100,246,255,0.28)";
      ctx.fill();
      roundedRectPath(ctx, chipX, chipY, chipW, chipH, 4);
      ctx.strokeStyle = "#9efcff";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#e9ffff";
      ctx.font = `bold 4px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("CLAIM", chipX + chipW / 2, chipY + chipH / 2);
    }

    // ── Stamp for claimed days ──
    if (isClaimed) {
      deps.drawDailyLoginStamp({ x: cx, y: cy, w: cw, h: ch });
    }
  }

  // ── Footer: sign in prompt for guests, or helper text ──
  const footerY = gridTopY + 2 * (cardH + gapY) + day7H + 8;

  if (isGuestUser) {
    ctx.fillStyle = "#a6b7ff";
    ctx.font = `${mobile ? 4 : 5}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("SIGN IN TO CLAIM DAILY REWARDS", panelX + panelW / 2, footerY);

    const signInRect: Rect = { x: panelX + 24, y: footerY + 12, w: panelW - 48, h: 26 };
    deps.drawGlowPulse(signInRect, "#f3c567", 16);
    deps.drawBtn(signInRect, "SIGN IN TO CLAIM", {
      fill: "#3a2910",
      stroke: "#f3c567",
      font: mFont("bold", 6),
      variant: "lobby",
    });

    ctx.restore();
    return {
      dailyLoginDismiss: dismissRect,
      dailyLoginClose: closeRect,
      dailyLoginSignIn: signInRect,
    };
  }

  // Helper text for logged-in users
  if (canClaim) {
    const helperPulse = 0.55 + 0.45 * Math.sin(deps.nowSec() * 3.1);
    ctx.fillStyle = `rgba(225,255,255,${(0.72 + helperPulse * 0.28).toFixed(3)})`;
    ctx.font = `bold ${mobile ? 5 : 5}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("TAP YOUR CRATE TO CLAIM!", panelX + panelW / 2, footerY);
  } else {
    ctx.fillStyle = "#7a94b8";
    ctx.font = `${mobile ? 4 : 5}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("COME BACK TOMORROW FOR YOUR NEXT REWARD", panelX + panelW / 2, footerY);
  }

  ctx.restore();
  return {
    dailyLoginDismiss: dismissRect,
    dailyLoginClose: closeRect,
    dailyLoginClaim: claimRect,
  };
}
